/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useMemo, useState} from "react"
import {BaseButton, BaseInput, BasePopup, BaseTextArea, Icon, SecondaryButton} from "components"
import styles from "./PopupAddMakeupClassroomAttendance.module.css"
import moment from "moment-timezone"
import {UploadedAttachment, useModel, useVisible} from "hooks"
import {convertTZ, getFullName, handleError, roundTwoDigits, toastSuccess} from "helpers"
import {academicPlansService, profileService} from "services"
import {Auth} from "types/auth"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import cx from "classnames"
import {Spin} from "antd"
import {useAttachmentsUploader} from "hooks"
import {useMutation} from "@tanstack/react-query"
import {SignaturePopup} from "uiKit"
import {BaseDatePicker} from "components/DateTimePicker"
import {FormLabel} from "components/Form"

type PopupAddMakeupClassroomAttendanceProps = {
    isShow: boolean
    onClose: () => void
    onRefreshData?: () => void
    startsAt: any
    lessonId: number
    profileId: number
    scheduleId: number
    termId: number
    courseId: number
    missedHours: number
    parentAttendanceId: number
}

const PopupAddMakeupClassroomAttendance = (props: PopupAddMakeupClassroomAttendanceProps) => {
    const model = useModel()

    const {
        isShow,
        onClose,
        onRefreshData,
        profileId,
        startsAt,
        lessonId,
        scheduleId,
        termId,
        courseId,
        missedHours,
        parentAttendanceId
    } = props
    const [notes, setNotes] = useState<string>("")
    const [isLoadingStudentInfo, setIsLoadingStudentInfo] = useState(false)
    const [studentSignature, setStudentSignature] = useState<{signatureId: number; imageUrl: string; userId: number}>()
    const [selectedStudent, setSelectedStudent] = useState<any>()
    const [appliedMinutes, setAppliedMinutes] = useState<any>()
    const instructorSignaturePopup = useVisible()

    const uploader = useAttachmentsUploader()
    const timezone = selectedStudent?.timezone || moment.tz.guess()
    const [makeupDate, setMakeupDate] = useState(moment().toISOString())

    const createMakeupAttendanceMutation = useMutation(
        async (instructorSignatureId: number) => {
            const createMakeupAttendance = async (attachments?: Array<UploadedAttachment>) => {
                const timezone = selectedStudent?.timezone || moment.tz.guess()

                await academicPlansService.createClassroomMakeupAttendance({
                    academicClassroomMakeupStudents: [
                        {
                            courseId,
                            termId,
                            scheduleId,
                            attachments,
                            lessonId,
                            createdAt: makeupDate,
                            studentProfileId: profileId,
                            calendarEventId: null,
                            reason: notes ?? "",
                            forceCreate: true,
                            appliedMinutes: appliedMinutes?.toString()?.length ? Number(appliedMinutes) : undefined,
                            instructorSignatureId,
                            advisorSignatureId: instructorSignatureId,
                            parentAttendanceId
                        }
                    ]
                })

                toastSuccess("Create makeup attendance successfully.")
                onRefreshData?.()
                resetState()
                onClose()
            }
            if (uploader.attachments.length) {
                uploader.upload({
                    onSuccess: (attachments) => createMakeupAttendance(attachments),
                    onError: (err) => handleError(err)
                })
            } else {
                await createMakeupAttendance()
            }
        },
        {
            onError: (err) => {
                handleError(err)
            }
        }
    )

    const onSaveInstructorSignature = async (signature) => {
        instructorSignaturePopup.close()
        if (signature.signatureId) {
            createMakeupAttendanceMutation.mutate(signature.signatureId)
        }
    }

    const resetState = () => {
        setNotes("")
        setStudentSignature(undefined)
    }

    const onCancel = useCallback(() => {
        resetState()
        onClose()
    }, [onClose])

    const getStudentInfo = useCallback(async () => {
        try {
            setIsLoadingStudentInfo(true)
            let {data} = await profileService.getAll({
                linkedObjects: true,
                filter: {
                    type: ["student"],
                    campuses: [],
                    id: [profileId]
                },
                range: {
                    limit: UNLIMITED_PAGE_SIZE,
                    offset: 0
                }
            })
            data = data.map((student) => ({
                ...student,
                photo:
                    typeof student.photo === "string" && student.photo?.length
                        ? JSON.parse(student.photo)
                        : student.photo,
                profileId: student.id
            }))
            const studentInfo = data[0]

            if (studentInfo) {
                const timezone = studentInfo?.timezone || moment.tz.guess()
                setMakeupDate(moment().tz(timezone).startOf("day").toISOString())
            }
            setSelectedStudent(studentInfo)
        } catch (e) {
            handleError(e)
        } finally {
            setIsLoadingStudentInfo(false)
        }
    }, [profileId])

    useEffect(() => {
        if (isShow) {
            getStudentInfo()
            setAppliedMinutes(`${roundTwoDigits((missedHours ?? 0) * 60)}`)
        }
    }, [profileId, isShow, missedHours])

    const renderUserInfo = useCallback(
        (user: Auth.UserProfile & {customProfileId?: number}, isStudent: boolean = false) => {
            let photo = "/image/DefaultAvatar.png"
            if (user?.photo) {
                photo = user.photo["256"]
            }

            return (
                <>
                    <p className={styles.label} style={isStudent ? {marginTop: 40} : {}}>
                        {isStudent ? "STUDENT" : "INSTRUCTOR"}
                    </p>
                    <div className={styles.instructorBox}>
                        <div className={styles.avatar}>
                            <img src={photo} alt="instructorAvt" />
                        </div>
                        <div className={styles.instructorDetail}>
                            <div className={styles.nameBox} style={{marginTop: 0}}>
                                <span className={styles.instructorName}>{getFullName(user)}</span>
                            </div>
                            <div
                                className={cx(styles.instructorInfo, {
                                    [styles.line]: isStudent
                                })}>
                                <div className={styles.nameBox} style={!isStudent ? {flex: "none"} : {}}>
                                    <span className={styles.labelUserId}>User ID</span>
                                    <span className={styles.valueUserId}>{user?.customUserId}</span>
                                </div>
                                {isStudent ? (
                                    <div className={styles.nameBox}>
                                        <span className={styles.labelUserId}>Enrollment ID:</span>
                                        <span className={styles.valueUserId}>{user?.customProfileId}</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </>
            )
        },
        [model, studentSignature]
    )

    const renderTitleInstructorSignature = useMemo(() => {
        return (
            <div className={styles.titleSignContainer}>
                <Icon icon={"SIGN_MAKEUP_ATTENDANCE"} />
                <span className={styles.titleSign}>INSTRUCTOR</span>
                <span className={styles.subTitleSign}>CONFIRM</span>
            </div>
        )
    }, [])

    return (
        <>
            <BasePopup
                isShow={isShow}
                onClose={() => {
                    resetState()
                    onClose()
                }}
                width="70vw"
                leftIcon="PERSON_ADD">
                <div className={styles.wrap}>
                    <p className={styles.title}>Student make up</p>
                    <div className={styles.header}>
                        {selectedStudent ? (
                            <>
                                <div>
                                    <FormLabel className={styles.label} label="Lesson Date" />
                                    <div className={styles.date}>
                                        <Icon
                                            icon="CALENDAR_ATTENDANCE"
                                            color="#62B1FF"
                                            className={styles.calendarIcon}
                                        />
                                        <p className={styles.textDate}>
                                            {moment(convertTZ(new Date(startsAt), timezone)).format(
                                                model.getUserDateFormat()
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <FormLabel className={styles.label} label="Makeup Date" />
                                    <BaseDatePicker
                                        className={styles.datePicker}
                                        format={model.getUserDateFormat()}
                                        value={makeupDate ? moment(makeupDate) : undefined}
                                        timezone={timezone}
                                        onChange={setMakeupDate}
                                    />
                                </div>
                            </>
                        ) : null}
                    </div>
                    {renderUserInfo(model.user)}

                    {isLoadingStudentInfo ? (
                        <div className={styles.spinnerContainer}>
                            <Spin />
                        </div>
                    ) : (
                        <>
                            {renderUserInfo(selectedStudent, true)}
                            <div className={styles.checkContainer}>
                                <span className={styles.label}>Notes</span>
                            </div>
                            <BaseTextArea
                                value={notes}
                                onChange={(text) => {
                                    setNotes(text)
                                }}
                                placeholder={"Notes"}
                                style={{
                                    width: "100%"
                                }}
                            />

                            <span className={styles.appliedMinutesTitle} style={{marginTop: 14, marginBottom: 8}}>
                                APPLIED MINUTES
                            </span>
                            <div className={styles.appliedMinutesInput}>
                                <BaseInput onChange={setAppliedMinutes} type="number" value={appliedMinutes} />
                            </div>
                        </>
                    )}
                    <div className={styles.buttonSubmit}>
                        <SecondaryButton title={"CANCEL"} onClick={onCancel} />
                        {selectedStudent ? (
                            <BaseButton
                                title={"SAVE"}
                                loading={createMakeupAttendanceMutation.isLoading}
                                onClick={instructorSignaturePopup.open}
                            />
                        ) : (
                            <div />
                        )}
                    </div>
                </div>

                {instructorSignaturePopup.isVisible && (
                    <SignaturePopup
                        isShow={instructorSignaturePopup.isVisible}
                        onClose={instructorSignaturePopup.close}
                        targetUser={{
                            type: Auth.UserProfileType.Staff,
                            id: model.user.userId,
                            profileId: model.user.profileId
                        }}
                        title=""
                        actions={{
                            onCancel: instructorSignaturePopup.close,
                            onSign: onSaveInstructorSignature
                        }}
                        autoClose
                        customTitle={renderTitleInstructorSignature}
                        onSetNewSignature={onSaveInstructorSignature}
                    />
                )}
            </BasePopup>
        </>
    )
}

export default PopupAddMakeupClassroomAttendance
